import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Paper, Typography, Grid, Card, CardContent, Stack,
  TextField, Switch, FormControlLabel, Button, Chip, Divider, Alert,
  Table, TableHead, TableRow, TableCell, TableBody, IconButton, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, Select, MenuItem,
  FormControl, InputLabel, Snackbar, Avatar, Tab, Tabs, LinearProgress,
  Collapse, InputAdornment, ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import ExtensionIcon from '@mui/icons-material/Extension';
import SettingsIcon from '@mui/icons-material/Settings';
import ViewListIcon from '@mui/icons-material/ViewList';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import SearchIcon from '@mui/icons-material/Search';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PreviewIcon from '@mui/icons-material/Preview';
import InboxIcon from '@mui/icons-material/Inbox';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import configService from '../services/configService';

// ── MUI Icon mapping ────────────────────────────────────────────────────────
import GroupsIcon from '@mui/icons-material/Groups';
import ArticleIcon from '@mui/icons-material/Article';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import BeachAccessIcon from '@mui/icons-material/BeachAccess';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import AssignmentIcon from '@mui/icons-material/Assignment';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import SchoolIcon from '@mui/icons-material/School';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import BarChartIcon from '@mui/icons-material/BarChart';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';

const ICON_MAP = {
  GroupsIcon, ArticleIcon, AccessTimeIcon, BeachAccessIcon,
  AccountBalanceIcon, FolderOpenIcon, AssignmentIcon, PersonAddIcon,
  SchoolIcon, ConfirmationNumberIcon, BarChartIcon, VerifiedUserIcon,
  ExtensionIcon, SettingsIcon, ViewListIcon, ViewModuleIcon,
};

const ICON_OPTIONS = Object.keys(ICON_MAP);

// ── Reusable Tooltip Helpers ────────────────────────────────────────────────
const TOOLTIP_PROPS = {
  enterDelay: 300,
  enterNextDelay: 200,
  arrow: true,
  slotProps: { tooltip: { sx: { fontSize: '0.75rem', maxWidth: 240, lineHeight: 1.4 } } },
};

function FieldTooltip({ title, placement = 'top', children }) {
  if (!title) return children || null;
  return (
    <Tooltip title={title} placement={placement} {...TOOLTIP_PROPS}>
      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ display: 'inline-flex' }}>
        {children}
        <HelpOutlineIcon sx={{ fontSize: 14, color: 'text.disabled', cursor: 'help' }} />
      </Stack>
    </Tooltip>
  );
}

function TipWrap({ title, placement = 'top', children }) {
  if (!title) return children;
  return <Tooltip title={title} placement={placement} {...TOOLTIP_PROPS}>{children}</Tooltip>;
}

const FIELD_TYPES = [
  { value: 'text',        label: 'Tekst' },
  { value: 'textarea',    label: 'Tekst i gjatë' },
  { value: 'number',      label: 'Numër' },
  { value: 'date',        label: 'Datë' },
  { value: 'select',      label: 'Zgjedhje' },
  { value: 'multiselect', label: 'Zgjedhje e shumëfishtë' },
  { value: 'checkbox',    label: 'Checkbox' },
  { value: 'radio',       label: 'Radio' },
  { value: 'boolean',     label: 'Po/Jo' },
  { value: 'email',       label: 'Email' },
  { value: 'phone',       label: 'Telefon' },
  { value: 'currency',    label: 'Valutë' },
];

function getIcon(name, props = {}) {
  const Icon = ICON_MAP[name] || ExtensionIcon;
  return <Icon {...props} />;
}

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('sq-AL', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

const KEY_REGEX = /^[a-z][a-z0-9_]*$/;

// ── Empty State ─────────────────────────────────────────────────────────────
function EmptyState({ icon, title, description, action }) {
  return (
    <Box sx={{ textAlign: 'center', py: 8, px: 3 }}>
      <Box sx={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: 72, height: 72, borderRadius: '50%', bgcolor: 'grey.100', mb: 2.5, color: 'text.disabled',
      }}>{icon}</Box>
      <Typography variant="h6" fontWeight={700} gutterBottom sx={{ color: 'text.primary' }}>{title}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 380, mx: 'auto', lineHeight: 1.6 }}>{description}</Typography>
      {action}
    </Box>
  );
}

// ── Confirm Dialog ──────────────────────────────────────────────────────────
function ConfirmDialog({ open, onClose, onConfirm, title, message, confirmLabel = 'Fshi', confirmColor = 'error' }) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <WarningAmberIcon color="warning" /> {title}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2">{message}</Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Anulo</Button>
        <Button variant="contained" color={confirmColor} onClick={() => { onConfirm(); onClose(); }}>{confirmLabel}</Button>
      </DialogActions>
    </Dialog>
  );
}

function SectionCard({ title, icon, children, action }) {
  return (
    <Card elevation={0} sx={{ borderRadius: 2.5, border: '1px solid', borderColor: 'divider', mb: 2, overflow: 'hidden' }}>
      <Box sx={{
        px: 2, py: 1.25, background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
        borderBottom: '1px solid', borderColor: 'divider',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          {icon}
          <Typography variant="subtitle2" fontWeight={700} sx={{ letterSpacing: 0.1 }}>{title}</Typography>
        </Stack>
        {action}
      </Box>
      <CardContent sx={{ p: '0 !important' }}>{children}</CardContent>
    </Card>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// MODULE LIST TAB
// ═══════════════════════════════════════════════════════════════════════════════
function ModuleListTab({ modules, onRefresh }) {
  const [editDialog, setEditDialog] = useState(null);
  const [createDialog, setCreateDialog] = useState(false);
  const [snack, setSnack] = useState({ msg: '', severity: 'success' });
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [previewModule, setPreviewModule] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewTab, setPreviewTab] = useState(0);
  const [keyError, setKeyError] = useState('');

  const toast = (msg, severity = 'success') => setSnack({ msg, severity });

  const filteredModules = useMemo(() => {
    let list = [...modules].sort((a, b) => a.sort_order - b.sort_order);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(m => m.name.toLowerCase().includes(q) || m.key.toLowerCase().includes(q));
    }
    if (filterStatus === 'active') list = list.filter(m => m.is_active);
    if (filterStatus === 'inactive') list = list.filter(m => !m.is_active);
    if (filterType === 'system') list = list.filter(m => m.is_system);
    if (filterType === 'custom') list = list.filter(m => m.is_custom);
    return list;
  }, [modules, search, filterStatus, filterType]);

  const validateKey = (key) => {
    if (!key) { setKeyError(''); return; }
    if (!KEY_REGEX.test(key)) { setKeyError('Vetëm shkronja të vogla, numra, dhe _ (duhet të fillojë me shkronjë)'); return; }
    if (modules.some(m => m.key === key)) { setKeyError('Ky çelës ekziston tashmë'); return; }
    setKeyError('');
  };

  const toggleActive = async (mod) => {
    try {
      await configService.updateModule(mod.id, { isActive: !mod.is_active });
      onRefresh();
      toast(mod.is_active ? 'Moduli u çaktivizua' : 'Moduli u aktivizua');
    } catch (e) { toast('Gabim: ' + e.message, 'error'); }
  };

  const moveModule = async (mod, direction) => {
    const sorted = [...modules].sort((a, b) => a.sort_order - b.sort_order);
    const idx = sorted.findIndex(m => m.id === mod.id);
    if ((direction === 'up' && idx === 0) || (direction === 'down' && idx === sorted.length - 1)) return;
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    const order = sorted.map((m, i) => ({
      id: m.id,
      sortOrder: i === idx ? sorted[swapIdx].sort_order : i === swapIdx ? sorted[idx].sort_order : m.sort_order,
    }));
    await configService.reorderModules(order);
    onRefresh();
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      await configService.updateModule(editDialog.id, {
        name: editDialog.name,
        description: editDialog.description,
        icon: editDialog.icon,
        color: editDialog.color,
      });
      setEditDialog(null);
      onRefresh();
      toast('Moduli u përditësua me sukses');
    } catch (e) { toast('Gabim: ' + e.message, 'error'); }
    setSaving(false);
  };

  const handleCreate = async () => {
    if (keyError) return;
    setSaving(true);
    try {
      await configService.createModule(editDialog);
      setCreateDialog(false);
      setEditDialog(null);
      onRefresh();
      toast('Moduli u krijua me sukses');
    } catch (e) { toast('Gabim: ' + e.message, 'error'); }
    setSaving(false);
  };

  const handleDelete = async (mod) => {
    try {
      await configService.deleteModule(mod.id);
      onRefresh();
      toast('Moduli u fshi me sukses');
    } catch (e) { toast('Gabim: ' + e.message, 'error'); }
  };

  const handleCloneModule = async (mod) => {
    setSaving(true);
    try {
      const full = await configService.getModule(mod.id);
      const src = full.data;
      const newKey = src.key + '_copy';
      const created = await configService.createModule({
        key: newKey, name: src.name + ' (Kopje)', description: src.description,
        icon: src.icon, color: src.color, isCustom: true,
      });
      const newModId = created.data.id;
      // clone sections
      const sectionMap = {};
      for (const sec of src.sections) {
        const r = await configService.createSection(newModId, { key: sec.key, label: sec.label, description: sec.description, icon: sec.icon });
        sectionMap[sec.id] = r.data.id;
      }
      // clone fields
      for (const f of src.fields) {
        await configService.createField(newModId, {
          key: f.key, label: f.label, fieldType: f.field_type,
          sectionId: f.section_id ? sectionMap[f.section_id] : null,
          isRequired: f.is_required, isActive: f.is_active, isCustom: true,
          placeholder: f.placeholder, helpText: f.help_text, defaultValue: f.default_value,
          width: f.width, showInCreate: f.show_in_create, showInEdit: f.show_in_edit,
          showInDetail: f.show_in_detail, showInList: f.show_in_list,
          isReadonly: f.is_readonly, isSortable: f.is_sortable, isSearchable: f.is_searchable, isExportable: f.is_exportable,
          options: f.options || [],
        });
      }
      onRefresh();
      toast('Moduli u klonua me sukses');
    } catch (e) { toast('Gabim gjatë klonimit: ' + e.message, 'error'); }
    setSaving(false);
  };

  const openPreview = async (mod) => {
    setPreviewModule(mod);
    setPreviewLoading(true);
    setPreviewTab(0);
    try {
      const r = await configService.getModule(mod.id);
      setPreviewData(r.data);
    } catch (e) { toast('Gabim: ' + e.message, 'error'); }
    setPreviewLoading(false);
  };

  return (
    <Box>
      {/* Search & Filters */}
      <Stack direction="row" spacing={1.5} sx={{ mb: 2.5 }} flexWrap="wrap" alignItems="center">
        <TextField
          size="small" placeholder="Kërko module..." value={search}
          onChange={e => setSearch(e.target.value)}
          sx={{ minWidth: 220 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: 'text.disabled' }} /></InputAdornment> }}
        />
        <ToggleButtonGroup size="small" value={filterStatus} exclusive onChange={(_, v) => v && setFilterStatus(v)}
          sx={{ '& .MuiToggleButton-root': { textTransform: 'none', px: 1.75, fontSize: '0.78rem', borderRadius: 1.5, '&.Mui-selected': { bgcolor: 'primary.main', color: '#fff', '&:hover': { bgcolor: 'primary.dark' } } } }}>
          <ToggleButton value="all">Të gjitha</ToggleButton>
          <ToggleButton value="active">Aktive</ToggleButton>
          <ToggleButton value="inactive">Joaktive</ToggleButton>
        </ToggleButtonGroup>
        <ToggleButtonGroup size="small" value={filterType} exclusive onChange={(_, v) => v && setFilterType(v)}
          sx={{ '& .MuiToggleButton-root': { textTransform: 'none', px: 1.75, fontSize: '0.78rem', borderRadius: 1.5, '&.Mui-selected': { bgcolor: 'secondary.main', color: '#fff', '&:hover': { bgcolor: 'secondary.dark' } } } }}>
          <ToggleButton value="all">Të gjitha</ToggleButton>
          <ToggleButton value="system">Sistem</ToggleButton>
          <ToggleButton value="custom">Custom</ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      <SectionCard
        title="Modulet e sistemit"
        icon={<ExtensionIcon sx={{ fontSize: 18, color: 'primary.main' }} />}
        action={
          <TipWrap title="Krijo një modul të ri custom në sistem"><Button size="small" variant="contained" startIcon={<AddIcon />}
            onClick={() => { setCreateDialog(true); setKeyError(''); setEditDialog({ key: '', name: '', description: '', icon: 'ExtensionIcon', color: '#3E8FA3', isCustom: true }); }}
            sx={{ textTransform: 'none', borderRadius: 2, boxShadow: 'none', '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.15)' } }}>
            Modul i ri
          </Button></TipWrap>
        }
      >
        {filteredModules.length === 0 ? (
          <EmptyState
            icon={<InboxIcon sx={{ fontSize: 36 }} />}
            title="Asnjë modul"
            description={search || filterStatus !== 'all' || filterType !== 'all'
              ? 'Asnjë modul nuk përputhet me filtrat e zgjedhur. Provoni të ndryshoni kriteret.'
              : 'Sistemi nuk ka module ende. Krijoni modulin tuaj të parë për të filluar.'}
            action={!search && filterStatus === 'all' && filterType === 'all' && (
              <Button variant="contained" startIcon={<AddIcon />}
                onClick={() => { setCreateDialog(true); setKeyError(''); setEditDialog({ key: '', name: '', description: '', icon: 'ExtensionIcon', color: '#3E8FA3', isCustom: true }); }}
                sx={{ textTransform: 'none', borderRadius: 2, boxShadow: 'none' }}>
                Krijo modulin e parë
              </Button>
            )}
          />
        ) : (
        <Box sx={{ p: 0 }}>
          {filteredModules.map((mod, idx) => (
            <Box
              key={mod.id}
              sx={{
                display: 'flex', alignItems: 'center', gap: 2,
                px: 2, py: 1.5,
                borderBottom: idx < filteredModules.length - 1 ? '1px solid' : 'none',
                borderColor: 'divider',
                opacity: mod.is_active ? 1 : 0.55,
                transition: 'background 0.15s',
                '&:hover': { bgcolor: 'grey.50' },
              }}
            >
              {/* Sort arrows */}
              <Stack spacing={0} sx={{ flexShrink: 0 }}>
                <TipWrap title="Lëviz lart"><IconButton size="small" onClick={() => moveModule(mod, 'up')} sx={{ p: 0.2, color: 'text.disabled', '&:hover': { color: 'primary.main' } }}>
                  <ArrowUpwardIcon sx={{ fontSize: 13 }} />
                </IconButton></TipWrap>
                <TipWrap title="Lëviz poshtë"><IconButton size="small" onClick={() => moveModule(mod, 'down')} sx={{ p: 0.2, color: 'text.disabled', '&:hover': { color: 'primary.main' } }}>
                  <ArrowDownwardIcon sx={{ fontSize: 13 }} />
                </IconButton></TipWrap>
              </Stack>

              {/* Avatar */}
              <Avatar sx={{
                bgcolor: mod.color || '#3E8FA3', width: 40, height: 40, flexShrink: 0,
                boxShadow: `0 2px 8px ${(mod.color || '#3E8FA3')}40`,
              }}>
                {getIcon(mod.icon, { sx: { fontSize: 20, color: '#fff' } })}
              </Avatar>

              {/* Name + description */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" alignItems="center" spacing={0.75} flexWrap="wrap">
                  <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.875rem' }}>{mod.name}</Typography>
                  {mod.is_system && <Chip label="Sistem" size="small" color="info" variant="outlined" sx={{ height: 18, fontSize: '0.6rem', '& .MuiChip-label': { px: 0.75 } }} />}
                  {mod.is_custom && <Chip label="Custom" size="small" color="secondary" variant="outlined" sx={{ height: 18, fontSize: '0.6rem', '& .MuiChip-label': { px: 0.75 } }} />}
                </Stack>
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mt: 0.25 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }} noWrap>
                    {mod.description || '—'}
                  </Typography>
                  <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.disabled', fontSize: '0.65rem', flexShrink: 0, bgcolor: 'grey.100', px: 0.75, py: 0.15, borderRadius: 0.75 }}>
                    {mod.key}
                  </Typography>
                </Stack>
              </Box>

              {/* Stats */}
              <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
                <Box sx={{ textAlign: 'center', minWidth: 40 }}>
                  <Typography variant="body2" fontWeight={700} sx={{ color: 'info.main', lineHeight: 1 }}>{mod.field_count || 0}</Typography>
                  <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.6rem' }}>fusha</Typography>
                </Box>
                <Divider orientation="vertical" flexItem />
                <Box sx={{ textAlign: 'center', minWidth: 46 }}>
                  <Typography variant="body2" fontWeight={700} sx={{ lineHeight: 1, color: 'text.secondary' }}>{mod.section_count || 0}</Typography>
                  <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.6rem' }}>seksi.</Typography>
                </Box>
              </Stack>

              {/* Status */}
              <Box sx={{ flexShrink: 0, minWidth: 70 }}>
                <Chip
                  size="small"
                  icon={mod.is_active ? <CheckCircleIcon /> : <CancelIcon />}
                  label={mod.is_active ? 'Aktiv' : 'Joaktiv'}
                  color={mod.is_active ? 'success' : 'default'}
                  sx={{ fontWeight: 600, fontSize: '0.7rem' }}
                />
              </Box>

              {/* Actions */}
              <Stack direction="row" spacing={0.25} sx={{ flexShrink: 0 }}>
                <Tooltip title="Pamja paraprake" {...TOOLTIP_PROPS}>
                  <IconButton size="small" onClick={() => openPreview(mod)} sx={{ p: 0.5, color: 'text.secondary', '&:hover': { color: 'primary.main', bgcolor: 'primary.50' } }}>
                    <PreviewIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Ndrysho" {...TOOLTIP_PROPS}>
                  <IconButton size="small" onClick={() => setEditDialog({ id: mod.id, name: mod.name, description: mod.description || '', icon: mod.icon, color: mod.color })}
                    sx={{ p: 0.5, color: 'text.secondary', '&:hover': { color: 'primary.main', bgcolor: 'primary.50' } }}>
                    <EditIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Klono" {...TOOLTIP_PROPS}>
                  <IconButton size="small" onClick={() => handleCloneModule(mod)} disabled={saving}
                    sx={{ p: 0.5, color: 'text.secondary', '&:hover': { color: 'secondary.main', bgcolor: 'secondary.50' } }}>
                    <ContentCopyIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title={mod.is_active ? 'Çaktivizo' : 'Aktivizo'} {...TOOLTIP_PROPS}>
                  <IconButton size="small" onClick={() => toggleActive(mod)}
                    sx={{ p: 0.5, color: mod.is_active ? 'warning.main' : 'success.main', '&:hover': { bgcolor: mod.is_active ? 'warning.50' : 'success.50' } }}>
                    {mod.is_active ? <VisibilityOffIcon sx={{ fontSize: 16 }} /> : <VisibilityIcon sx={{ fontSize: 16 }} />}
                  </IconButton>
                </Tooltip>
                {!mod.is_system && (
                  <Tooltip title="Fshi" {...TOOLTIP_PROPS}>
                    <IconButton size="small" color="error" onClick={() => setDeleteConfirm(mod)}
                      sx={{ p: 0.5, '&:hover': { bgcolor: 'error.50' } }}>
                      <DeleteIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            </Box>
          ))}
        </Box>
        )}
      </SectionCard>

      {/* Edit Module Dialog */}
      <Dialog open={!!editDialog && !createDialog} onClose={() => setEditDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Ndrysho modulin</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Emri" value={editDialog?.name || ''} onChange={e => setEditDialog(p => ({ ...p, name: e.target.value }))} fullWidth size="small" helperText="Emri i modulit që shfaqet në sidebar dhe faqe" />
            <TextField label="Përshkrimi" value={editDialog?.description || ''} onChange={e => setEditDialog(p => ({ ...p, description: e.target.value }))} fullWidth size="small" multiline rows={2} helperText="Përshkrim i shkurtër për qëllimin e modulit" />
            <FormControl size="small" fullWidth>
              <InputLabel>Ikona</InputLabel>
              <Select value={editDialog?.icon || 'ExtensionIcon'} label="Ikona" onChange={e => setEditDialog(p => ({ ...p, icon: e.target.value }))}>
                {ICON_OPTIONS.map(ico => (
                  <MenuItem key={ico} value={ico}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      {getIcon(ico, { sx: { fontSize: 18 } })}
                      <span>{ico.replace('Icon', '')}</span>
                    </Stack>
                  </MenuItem>
                ))}
              </Select>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.5 }}>Ikona që shfaqet në sidebar</Typography>
            </FormControl>
            <TextField label="Ngjyra" type="color" value={editDialog?.color || '#3E8FA3'} onChange={e => setEditDialog(p => ({ ...p, color: e.target.value }))} size="small" sx={{ width: 120 }} helperText="Ngjyra kryesore e identitetit vizual" />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog(null)}>Anulo</Button>
          <TipWrap title="Ruaj ndryshimet e modulit"><Button variant="contained" startIcon={<SaveIcon />} onClick={handleSaveEdit} disabled={saving}>Ruaj</Button></TipWrap>
        </DialogActions>
      </Dialog>

      {/* Create Module Dialog */}
      <Dialog open={createDialog} onClose={() => { setCreateDialog(false); setEditDialog(null); }} maxWidth="sm" fullWidth>
        <DialogTitle>Krijo modul të ri</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Çelësi (key)" value={editDialog?.key || ''}
              onChange={e => {
                const v = e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '');
                setEditDialog(p => ({ ...p, key: v }));
                validateKey(v);
              }}
              fullWidth size="small"
              error={!!keyError}
              helperText={keyError || 'Identifikues unik i modulit (p.sh. inventory, workers)'}
            />
            <TextField label="Emri" value={editDialog?.name || ''} onChange={e => setEditDialog(p => ({ ...p, name: e.target.value }))} fullWidth size="small" helperText="Emri i modulit që shfaqet në sidebar dhe faqe" />
            <TextField label="Përshkrimi" value={editDialog?.description || ''} onChange={e => setEditDialog(p => ({ ...p, description: e.target.value }))} fullWidth size="small" multiline rows={2} helperText="Përshkrim i shkurtër për qëllimin e modulit" />
            <FormControl size="small" fullWidth>
              <InputLabel>Ikona</InputLabel>
              <Select value={editDialog?.icon || 'ExtensionIcon'} label="Ikona" onChange={e => setEditDialog(p => ({ ...p, icon: e.target.value }))}>
                {ICON_OPTIONS.map(ico => (
                  <MenuItem key={ico} value={ico}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      {getIcon(ico, { sx: { fontSize: 18 } })}
                      <span>{ico.replace('Icon', '')}</span>
                    </Stack>
                  </MenuItem>
                ))}
              </Select>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.5 }}>Ikona që shfaqet në sidebar</Typography>
            </FormControl>
            <TextField label="Ngjyra" type="color" value={editDialog?.color || '#3E8FA3'} onChange={e => setEditDialog(p => ({ ...p, color: e.target.value }))} size="small" sx={{ width: 120 }} helperText="Ngjyra kryesore e identitetit vizual" />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setCreateDialog(false); setEditDialog(null); }}>Anulo</Button>
          <TipWrap title="Krijo modulin e ri në sistem"><Button variant="contained" startIcon={<AddIcon />} onClick={handleCreate} disabled={saving || !editDialog?.key || !editDialog?.name || !!keyError}>Krijo</Button></TipWrap>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={() => handleDelete(deleteConfirm)}
        title="Fshi modulin"
        message={`Jeni të sigurt që doni të fshini modulin "${deleteConfirm?.name}"? Kjo veprim nuk mund të kthehet mbrapa. Të gjitha fushat dhe seksionet e modulit do të fshihen gjithashtu.`}
      />

      {/* Preview Dialog */}
      <Dialog open={!!previewModule} onClose={() => { setPreviewModule(null); setPreviewData(null); }} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PreviewIcon /> Pamja paraprake — {previewModule?.name}
        </DialogTitle>
        <DialogContent>
          {previewLoading && <LinearProgress sx={{ mb: 2 }} />}
          {previewData && (
            <>
              <Tabs value={previewTab} onChange={(_, v) => setPreviewTab(v)} sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
                <Tab label="Formulari (Krijim)" sx={{ textTransform: 'none' }} />
                <Tab label="Detajet" sx={{ textTransform: 'none' }} />
                <Tab label="Lista (Tabelë)" sx={{ textTransform: 'none' }} />
              </Tabs>
              {previewTab === 0 && (
                <PreviewForm fields={previewData.fields.filter(f => f.show_in_create && f.is_active)} sections={previewData.sections} />
              )}
              {previewTab === 1 && (
                <PreviewDetail fields={previewData.fields.filter(f => f.show_in_detail && f.is_active)} sections={previewData.sections} />
              )}
              {previewTab === 2 && (
                <PreviewList fields={previewData.fields.filter(f => f.show_in_list && f.is_active)} />
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setPreviewModule(null); setPreviewData(null); }}>Mbyll</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!snack.msg} autoHideDuration={3000} onClose={() => setSnack({ msg: '', severity: 'success' })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={() => setSnack({ msg: '', severity: 'success' })} severity={snack.severity} variant="filled" sx={{ width: '100%' }}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}

// ── Preview Components ──────────────────────────────────────────────────────
function PreviewForm({ fields, sections }) {
  const grouped = {};
  sections.forEach(s => { grouped[s.id] = { label: s.label, fields: [] }; });
  grouped['_none'] = { label: 'Pa seksion', fields: [] };
  fields.forEach(f => {
    const key = f.section_id && grouped[f.section_id] ? f.section_id : '_none';
    grouped[key].fields.push(f);
  });

  return (
    <Box sx={{ border: '1px dashed', borderColor: 'divider', borderRadius: 2, p: 2, bgcolor: 'grey.50' }}>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>Kjo është një pamje paraprake. Fushat janë vetëm për demonstrim.</Typography>
      {Object.entries(grouped).map(([key, group]) => {
        if (group.fields.length === 0) return null;
        return (
          <Box key={key} sx={{ mb: 3 }}>
            {key !== '_none' && <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5, borderBottom: '1px solid', borderColor: 'divider', pb: 0.5 }}>{group.label}</Typography>}
            <Grid container spacing={2}>
              {group.fields.sort((a, b) => a.sort_order - b.sort_order).map(f => (
                <Grid item xs={12} sm={Number(f.width) || 6} key={f.id}>
                  {f.field_type === 'boolean' ? (
                    <FormControlLabel control={<Switch size="small" disabled={f.is_readonly} />} label={f.label} />
                  ) : f.field_type === 'select' || f.field_type === 'multiselect' ? (
                    <FormControl size="small" fullWidth disabled={f.is_readonly}>
                      <InputLabel>{f.label}</InputLabel>
                      <Select label={f.label} value="">
                        {(f.options || []).map((o, i) => <MenuItem key={i} value={o.value}>{o.label}</MenuItem>)}
                      </Select>
                      {f.help_text && <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.5 }}>{f.help_text}</Typography>}
                    </FormControl>
                  ) : f.field_type === 'textarea' ? (
                    <TextField label={f.label} placeholder={f.placeholder} helperText={f.help_text} fullWidth size="small" multiline rows={3} required={f.is_required} disabled={f.is_readonly} />
                  ) : (
                    <TextField
                      label={f.label} placeholder={f.placeholder} helperText={f.help_text}
                      fullWidth size="small" required={f.is_required} disabled={f.is_readonly}
                      type={f.field_type === 'number' || f.field_type === 'currency' ? 'number' : f.field_type === 'date' ? 'date' : f.field_type === 'email' ? 'email' : 'text'}
                      InputLabelProps={f.field_type === 'date' ? { shrink: true } : undefined}
                    />
                  )}
                </Grid>
              ))}
            </Grid>
          </Box>
        );
      })}
    </Box>
  );
}

function PreviewDetail({ fields, sections }) {
  const grouped = {};
  sections.forEach(s => { grouped[s.id] = { label: s.label, fields: [] }; });
  grouped['_none'] = { label: 'Pa seksion', fields: [] };
  fields.forEach(f => {
    const key = f.section_id && grouped[f.section_id] ? f.section_id : '_none';
    grouped[key].fields.push(f);
  });

  return (
    <Box sx={{ border: '1px dashed', borderColor: 'divider', borderRadius: 2, p: 2, bgcolor: 'grey.50' }}>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>Pamje e detajeve — vlera shembull</Typography>
      {Object.entries(grouped).map(([key, group]) => {
        if (group.fields.length === 0) return null;
        return (
          <Box key={key} sx={{ mb: 3 }}>
            {key !== '_none' && <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1, borderBottom: '1px solid', borderColor: 'divider', pb: 0.5 }}>{group.label}</Typography>}
            <Grid container spacing={1.5}>
              {group.fields.sort((a, b) => a.sort_order - b.sort_order).map(f => (
                <Grid item xs={12} sm={Number(f.width) || 6} key={f.id}>
                  <Typography variant="caption" color="text.secondary">{f.label}</Typography>
                  <Typography variant="body2" fontWeight={500} sx={{ color: 'text.primary' }}>
                    {f.default_value || '—'}
                  </Typography>
                </Grid>
              ))}
            </Grid>
          </Box>
        );
      })}
    </Box>
  );
}

function PreviewList({ fields }) {
  if (fields.length === 0) {
    return <Alert severity="info">Asnjë fushë nuk është konfiguruar për tu shfaqur në listë. Aktivizoni "Lista" te dukshmëria e fushave.</Alert>;
  }
  return (
    <Box sx={{ border: '1px dashed', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: 'grey.100' }}>
            <TableCell sx={{ fontWeight: 700, width: 50 }}>#</TableCell>
            {fields.sort((a, b) => a.sort_order - b.sort_order).map(f => (
              <TableCell key={f.id} sx={{ fontWeight: 700 }}>{f.label}</TableCell>
            ))}
            <TableCell sx={{ fontWeight: 700 }}>Veprime</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {[1, 2, 3].map(row => (
            <TableRow key={row} hover>
              <TableCell>{row}</TableCell>
              {fields.sort((a, b) => a.sort_order - b.sort_order).map(f => (
                <TableCell key={f.id}>
                  <Typography variant="body2" color="text.secondary">{f.default_value || '—'}</Typography>
                </TableCell>
              ))}
              <TableCell>
                <Stack direction="row" spacing={0.5}>
                  <IconButton size="small" disabled><VisibilityIcon sx={{ fontSize: 14 }} /></IconButton>
                  <IconButton size="small" disabled><EditIcon sx={{ fontSize: 14 }} /></IconButton>
                  <IconButton size="small" disabled><DeleteIcon sx={{ fontSize: 14 }} /></IconButton>
                </Stack>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// FIELD MANAGEMENT TAB
// ═══════════════════════════════════════════════════════════════════════════════
function FieldManagementTab({ modules }) {
  const [selectedModule, setSelectedModule] = useState(null);
  const [moduleConfig, setModuleConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [snack, setSnack] = useState({ msg: '', severity: 'success' });
  const [addFieldDialog, setAddFieldDialog] = useState(false);
  const [editFieldDialog, setEditFieldDialog] = useState(null);
  const [addSectionDialog, setAddSectionDialog] = useState(false);
  const [newField, setNewField] = useState({ key: '', label: '', fieldType: 'text', sectionId: null, width: '6', isRequired: false, isCustom: true, showInCreate: true, showInEdit: true, showInDetail: true, showInList: false, placeholder: '', helpText: '', options: [], isReadonly: false, isSortable: true, isSearchable: false, isExportable: true });
  const [newSection, setNewSection] = useState({ key: '', label: '', icon: '' });
  const [expandedSections, setExpandedSections] = useState({});
  const [saving, setSaving] = useState(false);
  const [fieldSearch, setFieldSearch] = useState('');
  const [fieldTypeFilter, setFieldTypeFilter] = useState('all');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleteSectionConfirm, setDeleteSectionConfirm] = useState(null);
  const [sectionKeyError, setSectionKeyError] = useState('');
  const [fieldKeyError, setFieldKeyError] = useState('');

  const toast = (msg, severity = 'success') => setSnack({ msg, severity });

  const loadModuleConfig = useCallback(async (modId) => {
    setLoading(true);
    try {
      const r = await configService.getModule(modId);
      if (r.success) setModuleConfig(r.data);
    } catch (e) { toast('Gabim: ' + e.message, 'error'); }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (selectedModule) loadModuleConfig(selectedModule);
  }, [selectedModule, loadModuleConfig]);

  const validateSectionKey = (key) => {
    if (!key) { setSectionKeyError(''); return; }
    if (!KEY_REGEX.test(key)) { setSectionKeyError('Vetëm shkronja të vogla, numra, dhe _'); return; }
    if (moduleConfig?.sections.some(s => s.key === key)) { setSectionKeyError('Ky çelës ekziston tashmë në këtë modul'); return; }
    setSectionKeyError('');
  };

  const validateFieldKey = (key, editId) => {
    if (!key) { setFieldKeyError(''); return; }
    if (!KEY_REGEX.test(key)) { setFieldKeyError('Vetëm shkronja të vogla, numra, dhe _'); return; }
    if (moduleConfig?.fields.some(f => f.key === key && f.id !== editId)) { setFieldKeyError('Ky çelës ekziston tashmë në këtë modul'); return; }
    setFieldKeyError('');
  };

  const handleAddField = async () => {
    if (fieldKeyError) return;
    setSaving(true);
    try {
      await configService.createField(selectedModule, newField);
      await loadModuleConfig(selectedModule);
      setAddFieldDialog(false);
      setNewField({ key: '', label: '', fieldType: 'text', sectionId: null, width: '6', isRequired: false, isCustom: true, showInCreate: true, showInEdit: true, showInDetail: true, showInList: false, placeholder: '', helpText: '', options: [], isReadonly: false, isSortable: true, isSearchable: false, isExportable: true });
      setFieldKeyError('');
      toast('Fusha u shtua me sukses');
    } catch (e) { toast('Gabim: ' + e.message, 'error'); }
    setSaving(false);
  };

  const handleUpdateField = async () => {
    if (fieldKeyError) return;
    setSaving(true);
    try {
      await configService.updateField(editFieldDialog.id, editFieldDialog);
      if (editFieldDialog.options && editFieldDialog.options.length > 0) {
        await configService.updateFieldOptions(editFieldDialog.id, editFieldDialog.options);
      }
      await loadModuleConfig(selectedModule);
      setEditFieldDialog(null);
      setFieldKeyError('');
      toast('Fusha u përditësua me sukses');
    } catch (e) { toast('Gabim: ' + e.message, 'error'); }
    setSaving(false);
  };

  const handleDeleteField = async (field) => {
    if (field.is_system) {
      toast('Fushat e sistemit nuk mund të fshihen. Çaktivizoni atë.', 'warning');
      return;
    }
    try {
      await configService.deleteField(field.id);
      await loadModuleConfig(selectedModule);
      toast('Fusha u fshi me sukses');
    } catch (e) { toast('Gabim: ' + e.message, 'error'); }
  };

  const handleCloneField = async (field) => {
    setSaving(true);
    try {
      await configService.createField(selectedModule, {
        key: field.key + '_copy', label: field.label + ' (Kopje)', fieldType: field.field_type,
        sectionId: field.section_id, isRequired: field.is_required, isActive: field.is_active, isCustom: true,
        placeholder: field.placeholder, helpText: field.help_text, defaultValue: field.default_value,
        width: field.width, showInCreate: field.show_in_create, showInEdit: field.show_in_edit,
        showInDetail: field.show_in_detail, showInList: field.show_in_list,
        isReadonly: field.is_readonly, isSortable: field.is_sortable, isSearchable: field.is_searchable, isExportable: field.is_exportable,
        options: field.options || [],
      });
      await loadModuleConfig(selectedModule);
      toast('Fusha u klonua me sukses');
    } catch (e) { toast('Gabim: ' + e.message, 'error'); }
    setSaving(false);
  };

  const handleCloneSection = async (section) => {
    setSaving(true);
    try {
      const r = await configService.createSection(selectedModule, {
        key: section.key + '_copy', label: section.label + ' (Kopje)',
        description: section.description, icon: section.icon,
      });
      const newSecId = r.data.id;
      const sectionFields = moduleConfig.fields.filter(f => f.section_id === section.id);
      for (const f of sectionFields) {
        await configService.createField(selectedModule, {
          key: f.key + '_copy', label: f.label, fieldType: f.field_type,
          sectionId: newSecId, isRequired: f.is_required, isActive: f.is_active, isCustom: true,
          placeholder: f.placeholder, helpText: f.help_text, defaultValue: f.default_value,
          width: f.width, showInCreate: f.show_in_create, showInEdit: f.show_in_edit,
          showInDetail: f.show_in_detail, showInList: f.show_in_list,
          isReadonly: f.is_readonly, isSortable: f.is_sortable, isSearchable: f.is_searchable, isExportable: f.is_exportable,
          options: f.options || [],
        });
      }
      await loadModuleConfig(selectedModule);
      toast('Seksioni u klonua me sukses');
    } catch (e) { toast('Gabim: ' + e.message, 'error'); }
    setSaving(false);
  };

  const handleToggleFieldVisibility = async (field, pageType) => {
    try {
      const update = { [pageType]: !field[pageType] };
      await configService.updateField(field.id, update);
      await loadModuleConfig(selectedModule);
    } catch (e) { toast('Gabim: ' + e.message, 'error'); }
  };

  const handleAddSection = async () => {
    if (sectionKeyError) return;
    setSaving(true);
    try {
      await configService.createSection(selectedModule, newSection);
      await loadModuleConfig(selectedModule);
      setAddSectionDialog(false);
      setNewSection({ key: '', label: '', icon: '' });
      setSectionKeyError('');
      toast('Seksioni u shtua me sukses');
    } catch (e) { toast('Gabim: ' + e.message, 'error'); }
    setSaving(false);
  };

  const handleDeleteSection = async (section) => {
    try {
      await configService.deleteSection(section.id);
      await loadModuleConfig(selectedModule);
      toast('Seksioni u fshi me sukses');
    } catch (e) { toast('Gabim: ' + e.message, 'error'); }
  };

  const moveField = async (field, direction) => {
    const fields = moduleConfig.fields.filter(f => f.section_id === field.section_id);
    const sorted = [...fields].sort((a, b) => a.sort_order - b.sort_order);
    const idx = sorted.findIndex(f => f.id === field.id);
    if ((direction === 'up' && idx === 0) || (direction === 'down' && idx === sorted.length - 1)) return;
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    const order = sorted.map((f, i) => ({
      id: f.id,
      sortOrder: i === idx ? sorted[swapIdx].sort_order : i === swapIdx ? sorted[idx].sort_order : f.sort_order,
    }));
    await configService.reorderFields(order);
    await loadModuleConfig(selectedModule);
  };

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({ ...prev, [sectionId]: !prev[sectionId] }));
  };

  const activeModules = modules.filter(m => m.is_active);

  // filter fields
  const filterFields = (fields) => {
    let list = fields;
    if (fieldSearch) {
      const q = fieldSearch.toLowerCase();
      list = list.filter(f => f.label.toLowerCase().includes(q) || f.key.toLowerCase().includes(q));
    }
    if (fieldTypeFilter !== 'all') {
      list = list.filter(f => f.field_type === fieldTypeFilter);
    }
    return list;
  };

  const selectedMod = activeModules.find(m => m.id === selectedModule);

  return (
    <Box sx={{ display: 'flex', height: '100%', minHeight: 0 }}>
      {/* ── Left sidebar: module list ── */}
      <Box sx={{ width: 240, minWidth: 240, borderRight: '1px solid', borderColor: 'divider', bgcolor: '#F7F8FA', display: { xs: 'none', md: 'flex' }, flexDirection: 'column' }}>
        <Box sx={{ p: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="caption" fontWeight={700} color="text.disabled" sx={{ textTransform: 'uppercase', letterSpacing: 0.8, fontSize: '0.62rem' }}>
            Modulet aktive
          </Typography>
        </Box>
        <Box sx={{ flex: 1, overflowY: 'auto', py: 0.5 }}>
          {activeModules.map(m => (
            <Box
              key={m.id}
              onClick={() => setSelectedModule(m.id)}
              sx={{
                mx: 0.75, my: 0.25, px: 1.25, py: 0.875, cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 1.25, borderRadius: 1.5,
                bgcolor: selectedModule === m.id ? 'primary.main' : 'transparent',
                '&:hover': { bgcolor: selectedModule === m.id ? 'primary.main' : 'rgba(0,0,0,0.04)' },
                transition: 'all 0.15s',
              }}
            >
              <Avatar sx={{
                bgcolor: selectedModule === m.id ? 'rgba(255,255,255,0.25)' : (m.color || '#3E8FA3'),
                width: 30, height: 30, flexShrink: 0,
                border: selectedModule === m.id ? '1px solid rgba(255,255,255,0.3)' : 'none',
              }}>
                {getIcon(m.icon, { sx: { fontSize: 15, color: '#fff' } })}
              </Avatar>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="body2" fontWeight={selectedModule === m.id ? 700 : 500} noWrap
                  sx={{ fontSize: '0.8rem', color: selectedModule === m.id ? '#fff' : 'text.primary' }}>
                  {m.name}
                </Typography>
                <Typography variant="caption" sx={{ fontSize: '0.62rem', color: selectedModule === m.id ? 'rgba(255,255,255,0.7)' : 'text.disabled' }}>
                  {m.field_count || 0} fusha · {m.section_count || 0} seksione
                </Typography>
              </Box>
              {selectedModule === m.id && (
                <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.7)', flexShrink: 0 }} />
              )}
            </Box>
          ))}
          {activeModules.length === 0 && (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="caption" color="text.disabled">Asnjë modul aktiv</Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* ── Right content: fields & sections ── */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {/* Mobile module selector */}
        <Box sx={{ display: { xs: 'block', md: 'none' }, p: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
          <FormControl size="small" fullWidth>
            <InputLabel>Zgjidh modulin</InputLabel>
            <Select value={selectedModule || ''} label="Zgjidh modulin" onChange={e => setSelectedModule(e.target.value)}>
              {activeModules.map(m => <MenuItem key={m.id} value={m.id}>{m.name}</MenuItem>)}
            </Select>
          </FormControl>
        </Box>
        {/* No module selected state */}
        {!selectedModule && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', py: 10, color: 'text.disabled' }}>
            <Box sx={{
              width: 80, height: 80, borderRadius: '50%', bgcolor: 'grey.100',
              display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2.5,
            }}>
              <ViewListIcon sx={{ fontSize: 36, opacity: 0.5 }} />
            </Box>
            <Typography variant="body1" fontWeight={600} color="text.secondary" gutterBottom>Zgjidhni një modul</Typography>
            <Typography variant="body2" color="text.disabled" sx={{ textAlign: 'center', maxWidth: 220 }}>
              Klikoni mbi një modul nga paneli majtas për të parë dhe menaxhuar fushat e tij
            </Typography>
          </Box>
        )}
        <Box sx={{ p: 2 }}>
          {/* Header bar */}
          {selectedModule && (
            <Box sx={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap',
              gap: 1, mb: 1.5, pb: 1.5, borderBottom: '1px solid', borderColor: 'divider',
            }}>
              <Stack direction="row" spacing={1.25} alignItems="center">
                {selectedMod && (
                  <Avatar sx={{ bgcolor: selectedMod.color, width: 32, height: 32, boxShadow: `0 2px 6px ${selectedMod.color}50` }}>
                    {getIcon(selectedMod.icon, { sx: { fontSize: 16, color: '#fff' } })}
                  </Avatar>
                )}
                <Box>
                  <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.2 }}>{selectedMod?.name || 'Modul'}</Typography>
                  {moduleConfig && (
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }}>
                      {moduleConfig.fields.length} fusha · {moduleConfig.sections.length} seksione
                    </Typography>
                  )}
                </Box>
              </Stack>
              <Stack direction="row" spacing={0.75}>
                <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => { setAddSectionDialog(true); setSectionKeyError(''); }}
                  sx={{ textTransform: 'none', fontSize: '0.75rem', borderRadius: 1.5, py: 0.4 }}>
                  Seksion
                </Button>
                <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={() => { setAddFieldDialog(true); setFieldKeyError(''); }}
                  sx={{ textTransform: 'none', fontSize: '0.75rem', borderRadius: 1.5, py: 0.4, boxShadow: 'none' }}>
                  Fushë
                </Button>
              </Stack>
            </Box>
          )}

          {/* Search & filter bar */}
          {selectedModule && moduleConfig && !loading && (
            <Stack direction="row" spacing={1} sx={{ mb: 1.5 }} alignItems="center">
              <TextField
                size="small" placeholder="Kërko fusha..." value={fieldSearch}
                onChange={e => setFieldSearch(e.target.value)} sx={{ flex: 1, maxWidth: 220 }}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16, color: 'text.disabled' }} /></InputAdornment> }}
              />
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel sx={{ fontSize: '0.8rem' }}>Lloji</InputLabel>
                <Select value={fieldTypeFilter} label="Lloji" onChange={e => setFieldTypeFilter(e.target.value)} sx={{ fontSize: '0.8rem' }}>
                  <MenuItem value="all">Të gjitha</MenuItem>
                  {FIELD_TYPES.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
                </Select>
              </FormControl>
            </Stack>
          )}

          {loading && <LinearProgress sx={{ mb: 1, borderRadius: 1 }} />}

          {moduleConfig && !loading && (
            <>
              {/* Empty module state */}
              {moduleConfig.sections.filter(s => s.is_active).length === 0 && moduleConfig.fields.length === 0 && (
                <Box sx={{ textAlign: 'center', py: 4, px: 2 }}>
                  <ViewModuleIcon sx={{ fontSize: 36, color: 'text.disabled', mb: 1 }} />
                  <Typography variant="body2" fontWeight={600} gutterBottom>Moduli është bosh</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>Shtoni seksione dhe fusha për të filluar.</Typography>
                  <Stack direction="row" spacing={1} justifyContent="center">
                    <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => { setAddSectionDialog(true); setSectionKeyError(''); }} sx={{ textTransform: 'none', fontSize: '0.75rem' }}>Seksion</Button>
                    <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={() => { setAddFieldDialog(true); setFieldKeyError(''); }} sx={{ textTransform: 'none', fontSize: '0.75rem' }}>Fushë</Button>
                  </Stack>
                </Box>
              )}

          {/* Sections with their fields */}
          {moduleConfig.sections.filter(s => s.is_active).map(section => {
            const allSectionFields = moduleConfig.fields.filter(f => f.section_id === section.id).sort((a, b) => a.sort_order - b.sort_order);
            const sectionFields = filterFields(allSectionFields);
            const isExpanded = expandedSections[section.id] !== false;

            return (
              <Card key={section.id} elevation={0} sx={{ mb: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
                <Box
                  sx={{
                    px: 1.75, py: 0.875,
                    background: 'linear-gradient(135deg, #f0f4f8 0%, #f8f9fa 100%)',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    cursor: 'pointer',
                    borderBottom: isExpanded ? '1px solid' : 'none', borderColor: 'divider',
                    '&:hover': { bgcolor: '#eef2f7' },
                  }}
                  onClick={() => toggleSection(section.id)}
                >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    {isExpanded ? <ExpandLessIcon sx={{ fontSize: 15, color: 'text.secondary' }} /> : <ExpandMoreIcon sx={{ fontSize: 15, color: 'text.secondary' }} />}
                    <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.8rem' }}>{section.label}</Typography>
                    <Chip label={allSectionFields.length} size="small" color="primary" variant="outlined" sx={{ height: 18, fontSize: '0.62rem', '& .MuiChip-label': { px: 0.75 } }} />
                  </Stack>
                  <Stack direction="row" spacing={0} onClick={e => e.stopPropagation()}>
                    <Tooltip title="Klono seksionin" {...TOOLTIP_PROPS}>
                      <IconButton size="small" onClick={() => handleCloneSection(section)} disabled={saving} sx={{ p: 0.3, color: 'text.disabled', '&:hover': { color: 'secondary.main' } }}>
                        <ContentCopyIcon sx={{ fontSize: 13 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Fshi seksionin" {...TOOLTIP_PROPS}>
                      <IconButton size="small" color="error" onClick={() => setDeleteSectionConfirm(section)} sx={{ p: 0.3, '&:hover': { bgcolor: 'error.50' } }}>
                        <DeleteIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Box>
                <Collapse in={isExpanded}>
                  {sectionFields.length === 0 && allSectionFields.length > 0 ? (
                    <Box sx={{ py: 1.5, textAlign: 'center' }}>
                      <Typography variant="caption" color="text.disabled">Asnjë përputhje me filtrat</Typography>
                    </Box>
                  ) : sectionFields.length === 0 ? (
                    <Box sx={{ py: 2, textAlign: 'center' }}>
                      <Typography variant="caption" color="text.disabled">Bosh</Typography>
                      <Box sx={{ mt: 0.5 }}>
                        <Button size="small" startIcon={<AddIcon />} onClick={() => { setAddFieldDialog(true); setFieldKeyError(''); setNewField(p => ({ ...p, sectionId: section.id })); }} sx={{ textTransform: 'none', fontSize: '0.7rem' }}>
                          Shto fushë
                        </Button>
                      </Box>
                    </Box>
                  ) : (
                  <Table size="small" sx={{ '& td, & th': { py: 0.5, px: 1, fontSize: '0.78rem' } }}>
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                        <TableCell sx={{ fontWeight: 600, width: 36, px: 0.5 }} />
                        <TableCell sx={{ fontWeight: 600 }}>Label</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Key</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Lloji</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>W</TableCell>
                        <TableCell sx={{ fontWeight: 600, textAlign: 'center' }}><FieldTooltip title="Dukshmëria: Krijim">C</FieldTooltip></TableCell>
                        <TableCell sx={{ fontWeight: 600, textAlign: 'center' }}><FieldTooltip title="Dukshmëria: Ndryshim">E</FieldTooltip></TableCell>
                        <TableCell sx={{ fontWeight: 600, textAlign: 'center' }}><FieldTooltip title="Dukshmëria: Detaje">D</FieldTooltip></TableCell>
                        <TableCell sx={{ fontWeight: 600, textAlign: 'center' }}><FieldTooltip title="Dukshmëria: Listë/Tabelë">L</FieldTooltip></TableCell>
                        <TableCell sx={{ fontWeight: 600, width: 100 }} />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sectionFields.map(field => (
                        <FieldRow key={field.id} field={field} onMove={moveField} onToggle={handleToggleFieldVisibility}
                          onEdit={(f) => { setEditFieldDialog(f); setFieldKeyError(''); }}
                          onDelete={setDeleteConfirm} onClone={handleCloneField} saving={saving} />
                      ))}
                    </TableBody>
                  </Table>
                  )}
                </Collapse>
              </Card>
            );
          })}

          {/* Unsectioned fields */}
          {(() => {
            const unsectioned = filterFields(moduleConfig.fields.filter(f => !f.section_id).sort((a, b) => a.sort_order - b.sort_order));
            if (unsectioned.length === 0 && moduleConfig.fields.filter(f => !f.section_id).length === 0) return null;
            if (unsectioned.length === 0) return (
              <Card sx={{ mb: 1.5, border: '1px dashed', borderColor: 'warning.300', borderRadius: 1.5 }}>
                <Box sx={{ px: 1.5, py: 0.75, bgcolor: '#fffde7' }}>
                  <Typography variant="body2" fontWeight={600} color="text.secondary" sx={{ fontSize: '0.78rem' }}>Pa seksion</Typography>
                </Box>
                <Box sx={{ py: 1.5, textAlign: 'center' }}><Typography variant="caption" color="text.disabled">Asnjë përputhje me filtrat</Typography></Box>
              </Card>
            );
            return (
              <Card sx={{ mb: 1.5, border: '1px dashed', borderColor: 'warning.300', borderRadius: 1.5, overflow: 'hidden' }}>
                <Box sx={{ px: 1.5, py: 0.75, bgcolor: '#fffde7' }}>
                  <Typography variant="body2" fontWeight={600} color="text.secondary" sx={{ fontSize: '0.78rem' }}>Pa seksion ({unsectioned.length})</Typography>
                </Box>
                <Table size="small" sx={{ '& td': { py: 0.4, px: 1, fontSize: '0.78rem' } }}>
                  <TableBody>
                    {unsectioned.map(field => (
                      <TableRow key={field.id} hover>
                        <TableCell>{field.label}</TableCell>
                        <TableCell><Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{field.key}</Typography></TableCell>
                        <TableCell><Chip label={FIELD_TYPES.find(t => t.value === field.field_type)?.label || field.field_type} size="small" variant="outlined" /></TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={0.5}>
                            <IconButton size="small" onClick={() => { setEditFieldDialog({ ...field, fieldType: field.field_type, sectionId: field.section_id, isRequired: field.is_required, isActive: field.is_active, helpText: field.help_text || '', showInCreate: field.show_in_create, showInEdit: field.show_in_edit, showInDetail: field.show_in_detail, showInList: field.show_in_list, isReadonly: field.is_readonly || false, isSortable: field.is_sortable !== false, isSearchable: field.is_searchable || false, isExportable: field.is_exportable !== false, options: field.options || [] }); setFieldKeyError(''); }}>
                              <EditIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                            <IconButton size="small" onClick={() => handleCloneField(field)} disabled={saving}>
                              <ContentCopyIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Card>
            );
          })()}
        </>
      )}

      {/* Add Field Dialog */}
      <FieldDialog
        open={addFieldDialog}
        onClose={() => { setAddFieldDialog(false); setFieldKeyError(''); }}
        field={newField}
        setField={setNewField}
        onSave={handleAddField}
        saving={saving}
        title="Shto fushë të re"
        saveLabel="Shto"
        sections={moduleConfig?.sections || []}
        keyError={fieldKeyError}
        onKeyChange={(key) => validateFieldKey(key)}
      />

      {/* Edit Field Dialog */}
      <FieldDialog
        open={!!editFieldDialog}
        onClose={() => { setEditFieldDialog(null); setFieldKeyError(''); }}
        field={editFieldDialog || {}}
        setField={setEditFieldDialog}
        onSave={handleUpdateField}
        saving={saving}
        title="Ndrysho fushën"
        saveLabel="Ruaj"
        sections={moduleConfig?.sections || []}
        keyError={fieldKeyError}
        onKeyChange={(key) => validateFieldKey(key, editFieldDialog?.id)}
      />

      {/* Add Section Dialog */}
      <Dialog open={addSectionDialog} onClose={() => setAddSectionDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Shto seksion të ri</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Çelësi (key)" value={newSection.key}
              onChange={e => {
                const v = e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '');
                setNewSection(p => ({ ...p, key: v }));
                validateSectionKey(v);
              }}
              fullWidth size="small" error={!!sectionKeyError}
              helperText={sectionKeyError || 'Identifikues i brendshëm për seksionin (p.sh. personal_info)'}
            />
            <TextField label="Emri (label)" value={newSection.label} onChange={e => setNewSection(p => ({ ...p, label: e.target.value }))} fullWidth size="small" helperText="Titulli i seksionit që shfaqet në formularë" />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddSectionDialog(false)}>Anulo</Button>
          <TipWrap title="Shto seksionin e ri në modul"><Button variant="contained" onClick={handleAddSection} disabled={saving || !newSection.key || !newSection.label || !!sectionKeyError}>Shto</Button></TipWrap>
        </DialogActions>
      </Dialog>

      {/* Delete field confirmation */}
      <ConfirmDialog
        open={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={() => handleDeleteField(deleteConfirm)}
        title="Fshi fushën"
        message={`Jeni të sigurt që doni të fshini fushën "${deleteConfirm?.label}"? Kjo veprim nuk mund të kthehet mbrapa.`}
      />

      {/* Delete section confirmation */}
      <ConfirmDialog
        open={!!deleteSectionConfirm}
        onClose={() => setDeleteSectionConfirm(null)}
        onConfirm={() => handleDeleteSection(deleteSectionConfirm)}
        title="Fshi seksionin"
        message={`Jeni të sigurt që doni të fshini seksionin "${deleteSectionConfirm?.label}"? Fushat brenda tij do të mbeten pa seksion.`}
      />

      <Snackbar open={!!snack.msg} autoHideDuration={3000} onClose={() => setSnack({ msg: '', severity: 'success' })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={() => setSnack({ msg: '', severity: 'success' })} severity={snack.severity} variant="filled" sx={{ width: '100%' }}>
          {snack.msg}
        </Alert>
      </Snackbar>
        </Box>
      </Box>
    </Box>
  );
}

// ── Field Row (extracted for cleanliness) ───────────────────────────────────
function FieldRow({ field, onMove, onToggle, onEdit, onDelete, onClone, saving }) {
  const openEdit = () => onEdit({
    ...field,
    sectionId: field.section_id,
    fieldType: field.field_type,
    isRequired: field.is_required,
    isActive: field.is_active,
    helpText: field.help_text || '',
    showInCreate: field.show_in_create,
    showInEdit: field.show_in_edit,
    showInDetail: field.show_in_detail,
    showInList: field.show_in_list,
    isReadonly: field.is_readonly || false,
    isSortable: field.is_sortable !== false,
    isSearchable: field.is_searchable || false,
    isExportable: field.is_exportable !== false,
    options: field.options || [],
  });

  return (
    <TableRow hover sx={{ opacity: field.is_active ? 1 : 0.4 }}>
      <TableCell sx={{ px: 0.25, width: 36 }}>
        <Stack spacing={0}>
          <IconButton size="small" onClick={() => onMove(field, 'up')} sx={{ p: 0.15 }}><ArrowUpwardIcon sx={{ fontSize: 11 }} /></IconButton>
          <IconButton size="small" onClick={() => onMove(field, 'down')} sx={{ p: 0.15 }}><ArrowDownwardIcon sx={{ fontSize: 11 }} /></IconButton>
        </Stack>
      </TableCell>
      <TableCell>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.78rem' }}>{field.label}</Typography>
          {field.is_required && <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: 'error.main', flexShrink: 0 }} />}
          {field.is_readonly && <Chip label="R/O" size="small" sx={{ height: 14, fontSize: '0.55rem', '& .MuiChip-label': { px: 0.5 } }} />}
        </Stack>
      </TableCell>
      <TableCell><Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.7rem', color: 'text.secondary' }}>{field.key}</Typography></TableCell>
      <TableCell><Chip label={FIELD_TYPES.find(t => t.value === field.field_type)?.label || field.field_type} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} /></TableCell>
      <TableCell sx={{ fontSize: '0.75rem' }}>{field.width}</TableCell>
      {[
        { key: 'showInCreate', val: field.show_in_create },
        { key: 'showInEdit', val: field.show_in_edit },
        { key: 'showInDetail', val: field.show_in_detail },
        { key: 'showInList', val: field.show_in_list },
      ].map(v => (
        <TableCell key={v.key} align="center" sx={{ px: 0.25 }}>
          <IconButton size="small" onClick={() => onToggle(field, v.key)} sx={{ p: 0.25, color: v.val ? 'success.main' : 'text.disabled' }}>
            {v.val ? <VisibilityIcon sx={{ fontSize: 14 }} /> : <VisibilityOffIcon sx={{ fontSize: 14 }} />}
          </IconButton>
        </TableCell>
      ))}
      <TableCell sx={{ px: 0.5 }}>
        <Stack direction="row" spacing={0}>
          <IconButton size="small" onClick={openEdit} sx={{ p: 0.25 }}><EditIcon sx={{ fontSize: 14 }} /></IconButton>
          <IconButton size="small" onClick={() => onClone(field)} disabled={saving} sx={{ p: 0.25 }}><ContentCopyIcon sx={{ fontSize: 12 }} /></IconButton>
          {!field.is_system && (
            <IconButton size="small" color="error" onClick={() => onDelete(field)} sx={{ p: 0.25 }}><DeleteIcon sx={{ fontSize: 14 }} /></IconButton>
          )}
        </Stack>
      </TableCell>
    </TableRow>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// FIELD DIALOG (shared between add and edit)
// ═══════════════════════════════════════════════════════════════════════════════
function FieldDialog({ open, onClose, field, setField, onSave, saving, title, saveLabel, sections, keyError, onKeyChange }) {
  const needsOptions = ['select', 'multiselect', 'radio'].includes(field?.fieldType);
  const [newOption, setNewOption] = useState({ label: '', value: '' });

  if (!field) return null;

  const addOption = () => {
    if (!newOption.label || !newOption.value) return;
    setField(p => ({ ...p, options: [...(p.options || []), { ...newOption, sortOrder: (p.options || []).length }] }));
    setNewOption({ label: '', value: '' });
  };

  const removeOption = (idx) => {
    setField(p => ({ ...p, options: (p.options || []).filter((_, i) => i !== idx) }));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={6}>
            <TextField label="Label" value={field.label || ''} onChange={e => setField(p => ({ ...p, label: e.target.value }))} fullWidth size="small" helperText="Emri i fushës që shfaqet te përdoruesit" />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Key" value={field.key || ''}
              onChange={e => {
                const v = e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '');
                setField(p => ({ ...p, key: v }));
                onKeyChange(v);
              }}
              fullWidth size="small" disabled={field.is_system}
              error={!!keyError}
              helperText={keyError || 'Identifikues unik i fushës (pa hapësira)'}
            />
          </Grid>
          <Grid item xs={4}>
            <FormControl size="small" fullWidth>
              <InputLabel>Lloji i fushës</InputLabel>
              <Select value={field.fieldType || 'text'} label="Lloji i fushës" onChange={e => setField(p => ({ ...p, fieldType: e.target.value }))}>
                {FIELD_TYPES.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
              </Select>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.5 }}>Si sillet fusha (tekst, numër, datë, etj.)</Typography>
            </FormControl>
          </Grid>
          <Grid item xs={4}>
            <FormControl size="small" fullWidth>
              <InputLabel>Seksioni</InputLabel>
              <Select value={field.sectionId || ''} label="Seksioni" onChange={e => setField(p => ({ ...p, sectionId: e.target.value || null }))}>
                <MenuItem value="">Pa seksion</MenuItem>
                {sections.map(s => <MenuItem key={s.id} value={s.id}>{s.label}</MenuItem>)}
              </Select>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.5 }}>Ku do të shfaqet kjo fushë në formular</Typography>
            </FormControl>
          </Grid>
          <Grid item xs={4}>
            <FormControl size="small" fullWidth>
              <InputLabel>Gjerësia</InputLabel>
              <Select value={field.width || '6'} label="Gjerësia" onChange={e => setField(p => ({ ...p, width: e.target.value }))}>
                {[3, 4, 6, 8, 12].map(w => <MenuItem key={w} value={String(w)}>{w}/12</MenuItem>)}
              </Select>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.5 }}>Madhësia e fushës (p.sh. 6/12 = gjysmë gjerësie)</Typography>
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <TextField label="Placeholder" value={field.placeholder || ''} onChange={e => setField(p => ({ ...p, placeholder: e.target.value }))} fullWidth size="small" helperText="Tekst shembull brenda fushës" />
          </Grid>
          <Grid item xs={6}>
            <TextField label="Tekst ndihmës" value={field.helpText || ''} onChange={e => setField(p => ({ ...p, helpText: e.target.value }))} fullWidth size="small" helperText="Udhëzim shtesë poshtë fushës" />
          </Grid>
          <Grid item xs={6}>
            <TextField label="Vlera default" value={field.defaultValue || ''} onChange={e => setField(p => ({ ...p, defaultValue: e.target.value }))} fullWidth size="small" helperText="Vlera që plotësohet automatikisht" />
          </Grid>
          <Grid item xs={6}>
            <Stack direction="row" spacing={2} flexWrap="wrap">
              <TipWrap title="Përdoruesi duhet ta plotësojë këtë fushë para se të ruajë" placement="right">
                <FormControlLabel control={<Switch checked={field.isRequired || false} onChange={e => setField(p => ({ ...p, isRequired: e.target.checked }))} size="small" />} label="E detyrueshme" />
              </TipWrap>
              <TipWrap title="Aktivizo ose çaktivizo fushën pa e fshirë" placement="right">
                <FormControlLabel control={<Switch checked={field.isActive !== false} onChange={e => setField(p => ({ ...p, isActive: e.target.checked }))} size="small" />} label="Aktive" />
              </TipWrap>
            </Stack>
          </Grid>

          {/* Field Rules */}
          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <FieldTooltip title="Kontrollo sjelljen e fushës në ndërfaqe" placement="right">
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>Rregullat e fushës</Typography>
            </FieldTooltip>
            <Stack direction="row" spacing={2} flexWrap="wrap">
              <TipWrap title="Fusha shfaqet por nuk mund të ndryshohet nga përdoruesi">
                <FormControlLabel control={<Switch checked={field.isReadonly || false} onChange={e => setField(p => ({ ...p, isReadonly: e.target.checked }))} size="small" />} label="Vetëm lexim" />
              </TipWrap>
              <TipWrap title="Lejo renditjen sipas kësaj fushe në tabelë">
                <FormControlLabel control={<Switch checked={field.isSortable !== false} onChange={e => setField(p => ({ ...p, isSortable: e.target.checked }))} size="small" />} label="E renditshme" />
              </TipWrap>
              <TipWrap title="Përfshije në kërkimin e shpejtë">
                <FormControlLabel control={<Switch checked={field.isSearchable || false} onChange={e => setField(p => ({ ...p, isSearchable: e.target.checked }))} size="small" />} label="E kërkueshme" />
              </TipWrap>
              <TipWrap title="Përfshije kur eksportohen të dhënat (Excel/CSV)">
                <FormControlLabel control={<Switch checked={field.isExportable !== false} onChange={e => setField(p => ({ ...p, isExportable: e.target.checked }))} size="small" />} label="E eksportueshme" />
              </TipWrap>
            </Stack>
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <FieldTooltip title="Kontrollo ku shfaqet kjo fushë në ndërfaqe" placement="right">
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>Dukshmëria sipas faqes</Typography>
            </FieldTooltip>
            <Stack direction="row" spacing={2}>
              <TipWrap title="Shfaqe fushën kur krijohet një regjistrim i ri">
                <FormControlLabel control={<Switch checked={field.showInCreate !== false} onChange={e => setField(p => ({ ...p, showInCreate: e.target.checked }))} size="small" />} label="Krijo" />
              </TipWrap>
              <TipWrap title="Shfaqe fushën kur ndryshohet një regjistrim">
                <FormControlLabel control={<Switch checked={field.showInEdit !== false} onChange={e => setField(p => ({ ...p, showInEdit: e.target.checked }))} size="small" />} label="Ndrysho" />
              </TipWrap>
              <TipWrap title="Shfaqe fushën në pamjen e detajeve">
                <FormControlLabel control={<Switch checked={field.showInDetail !== false} onChange={e => setField(p => ({ ...p, showInDetail: e.target.checked }))} size="small" />} label="Detajet" />
              </TipWrap>
              <TipWrap title="Shfaqe fushën si kolonë në tabelë">
                <FormControlLabel control={<Switch checked={field.showInList || false} onChange={e => setField(p => ({ ...p, showInList: e.target.checked }))} size="small" />} label="Lista" />
              </TipWrap>
            </Stack>
          </Grid>

          {needsOptions && (
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>Opsionet</Typography>
              {(field.options || []).map((opt, idx) => (
                <Stack key={idx} direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                  <Chip label={`${opt.label} (${opt.value})`} onDelete={() => removeOption(idx)} size="small" />
                </Stack>
              ))}
              <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                <TextField label="Label" value={newOption.label} onChange={e => setNewOption(p => ({ ...p, label: e.target.value }))} size="small" sx={{ flex: 1 }} />
                <TextField label="Vlera" value={newOption.value} onChange={e => setNewOption(p => ({ ...p, value: e.target.value }))} size="small" sx={{ flex: 1 }} />
                <Button size="small" variant="outlined" onClick={addOption} disabled={!newOption.label || !newOption.value}>Shto</Button>
              </Stack>
            </Grid>
          )}

          {/* Audit metadata */}
          {field.created_at && (
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Stack direction="row" spacing={3}>
                <Typography variant="caption" color="text.disabled">Krijuar: {fmtDate(field.created_at)}</Typography>
                {field.updated_at && <Typography variant="caption" color="text.disabled">Ndryshuar: {fmtDate(field.updated_at)}</Typography>}
              </Stack>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Anulo</Button>
        <TipWrap title="Ruaj konfigurimin e fushës"><Button variant="contained" onClick={onSave} disabled={saving || !field.label || !!keyError}>{saveLabel}</Button></TipWrap>
      </DialogActions>
    </Dialog>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN PAGE
// ═══════════════════════════════════════════════════════════════════════════════
export default function ModuleManagementPage() {
  const [tab, setTab] = useState(0);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadModules = useCallback(async () => {
    try {
      const r = await configService.getModules();
      if (r.success) setModules(r.data);
    } catch (e) { console.error('Failed to load modules:', e); }
    setLoading(false);
  }, []);

  useEffect(() => { loadModules(); }, [loadModules]);

  if (loading) return <Box sx={{ p: 3 }}><LinearProgress /></Box>;

  return (
    <Box sx={{ p: { xs: 1, sm: 1.5, md: 2 }, height: '100%' }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
        <Box>
          <Typography variant="h6" fontWeight={700} sx={{ fontSize: '1.1rem', lineHeight: 1.3 }}>Menaxhimi i Moduleve</Typography>
          <Typography variant="caption" color="text.secondary">Konfiguro modulet, fushat, seksionet, dhe pamjet</Typography>
        </Box>
      </Stack>

      <Paper elevation={0} sx={{ borderRadius: 2, overflow: 'hidden', border: '1px solid', borderColor: 'divider', display: 'flex', flexDirection: 'column', minHeight: 'calc(100vh - 160px)' }}>
        <Box sx={{ px: 1.5, bgcolor: '#FAFBFC', borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{
            minHeight: 40,
            '& .MuiTab-root': { minHeight: 40, py: 0, minWidth: 'auto', px: 2 },
            '& .MuiTabs-indicator': { height: 2.5, borderRadius: '2px 2px 0 0' },
          }}>
            <Tab label="Modulet" icon={<ExtensionIcon sx={{ fontSize: 15 }} />} iconPosition="start" sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.8rem', gap: 0.5 }} />
            <Tab label="Fushat & Seksionet" icon={<ViewListIcon sx={{ fontSize: 15 }} />} iconPosition="start" sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.8rem', gap: 0.5 }} />
          </Tabs>
        </Box>
        <Box sx={{ flex: 1, minHeight: 0 }}>
          {tab === 0 && <Box sx={{ p: 2 }}><ModuleListTab modules={modules} onRefresh={loadModules} /></Box>}
          {tab === 1 && <FieldManagementTab modules={modules} />}
        </Box>
      </Paper>
    </Box>
  );
}
